/**
 * Boxes 8 and 9 of the ICS 206-CG, the two signatures the printed form asks
 * for: the Medical Unit Leader prepares it and the Safety Officer reviews it.
 *
 * Both are collected on the version bar above, as on every versioned form. The
 * Safety Officer's is appended to the signed tip rather than opening a version
 * of its own, which is why it is matched by role rather than by position: only
 * the first signature is the preparer's.
 *
 * The Coast Guard sheet prints "Reviewed by" where the docx descriptor the slot
 * is read off says "Approved by", so the stored role stays 'Approved By'.
 */

import type React from 'react';
import { useMemo } from 'react';

import { useFormVersionTip } from 'components/IAP/hooks/icsFormVersionHooks';
import { Ics204CardSection } from './AssignmentList/ics204BoxLabels';
import {
  formatSignatureDateTime,
  parseWalaSignatures,
  type WalaSignature,
} from './AssignmentList/ics204SignatureSlots';
import { ICS206_BOX_LABELS } from './ics206BoxLabels';

const REVIEWED_BY_ROLE = 'Approved By';

export type Ics206SignatureBoxesProps = {
  periodNumber: number;
};

const Line: React.FC<{ label: string; value: string; italic?: boolean }> = ({
  label,
  value,
  italic,
}) => (
  <div className={italic ? 'tw:italic' : undefined}>
    <span
      className={
        italic
          ? 'tw:not-italic tw:text-muted-foreground'
          : 'tw:text-muted-foreground'
      }
    >
      {label}:{' '}
    </span>
    {value}
  </div>
);

const SignatureBox: React.FC<{
  boxLabel: string;
  testId: string;
  signature: WalaSignature | undefined;
  unsignedMessage: string;
}> = ({ boxLabel, testId, signature, unsignedMessage }) => (
  <Ics204CardSection
    boxLabel={boxLabel}
    testId={testId}
    className="tw:mt-0 tw:h-full tw:min-w-0"
  >
    {signature ? (
      <div className="tw:grid tw:gap-1 tw:text-sm">
        <Line label="Name" value={signature.name} />
        <Line
          label="Position / Title"
          // The roster seat the signer held, resolved by the server. Older
          // signatures predate it and fall back to the slot they filled.
          value={signature.positionTitle || signature.role}
        />
        <Line label="Signature" value={signature.name} italic />
        <Line
          label="Date / Time"
          value={formatSignatureDateTime(signature.signedAt)}
        />
      </div>
    ) : (
      <div className="tw:text-sm tw:text-muted-foreground">
        {unsignedMessage}
      </div>
    )}
  </Ics204CardSection>
);

export const Ics206SignatureBoxes: React.FC<Ics206SignatureBoxesProps> = ({
  periodNumber,
}) => {
  const { data: tip } = useFormVersionTip('ICS_206', periodNumber);
  const signatures = useMemo(
    () => parseWalaSignatures(tip?.signatures),
    [tip?.signatures]
  );

  const preparedBy = signatures[0];
  const reviewedBy = signatures.find(
    (signature, index) => index > 0 && signature.role === REVIEWED_BY_ROLE
  );

  return (
    <div className="tw:grid tw:gap-3 tw:md:grid-cols-2">
      <SignatureBox
        boxLabel={ICS206_BOX_LABELS.preparedBy}
        testId="ics206-signature-prepared-by"
        signature={preparedBy}
        unsignedMessage="Not signed. Create a signed version above to sign this form."
      />
      <SignatureBox
        boxLabel={ICS206_BOX_LABELS.reviewedBy}
        testId="ics206-signature-reviewed-by"
        signature={reviewedBy}
        unsignedMessage={
          preparedBy
            ? 'Not reviewed. Sign as Reviewed by on the version bar above.'
            : 'Not reviewed. The Medical Unit Leader signs first.'
        }
      />
    </div>
  );
};

export default Ics206SignatureBoxes;
